import { enableProdMode, provideZonelessChangeDetection } from '@angular/core';
import { environment } from './environments/environment';
import { INTERCEPTORS } from '@app/core/interceptors/inteceptors';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { provideState, provideStore } from '@ngrx/store';
import { reducers, storeConfig } from '@app/core/ngrx';
import { provideRouterStore } from '@ngrx/router-store';
import { provideEffects } from '@ngrx/effects';
import { AppEffects } from '@app/core/ngrx/app.effects';
import { provideRouter, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import * as stationReducer from '@app/station/_ngrx/station.reducer';
import * as tourReducer from '@app/tour/_ngrx/tour.reducer';
import * as exhibitionReducer from '@app/exhibition/_ngrx/exhibition.reducer';
import * as contentReducer from '@app/content/_ngrx/content.reducer';

import { cmsConfig } from '@assets/config/feature/cms.config';
import { StationEffects } from '@app/station/_ngrx/station.effects';
import { TourEffects } from '@app/tour/_ngrx/tour.effects';
import { ContentEffects } from '@app/content/_ngrx/content.effects';
import { ExhibitionEffects } from '@app/exhibition/_ngrx/exhibition.effects';
import { appRoutes } from '@app/app-routes';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { ExhibitionFacade } from '@app/exhibition/exhibition.facade';
import { TourFacade } from '@app/tour/tour.facade';
import { StationFacade } from '@app/station/station.facade';
import { ContentFacade } from '@app/content/content.facade';
import { provideTranslateService, TranslateService, TranslateStore } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideServiceWorker } from '@angular/service-worker';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from '@app/app.component';

import { ApiModule as RestApiModule, Configuration as RestConfiguration } from '@api/rest';
import { ApiModule as OrganizationApiModule, Configuration as OrganizationConfiguration } from '@api/organization';
import { ApiModule as SyncApiModule, Configuration as SyncConfiguration } from '@api/sync';
import { ApiModule as KeycloakApiModule, Configuration as KeycloakConfiguration } from '@api/keycloak';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    ...INTERCEPTORS,
    provideZonelessChangeDetection(),
    provideMatomo({
        scriptUrl: 'https://providence.nousdigital.net/matomo.js',
        trackerUrl: 'https://providence.nousdigital.net/matomo.php',
        siteId: environment.matomoSiteID
    }, withRouter()),
    provideStore(reducers, storeConfig),
    provideRouterStore({ stateKey: 'router' }),
    provideEffects(AppEffects),
    provideRouter(appRoutes, withViewTransitions(), withInMemoryScrolling({scrollPositionRestoration: 'top'})),
    provideHttpClient(withInterceptorsFromDi()),

    provideState(stationReducer.stationsFeatureKey, stationReducer.reducer ), // Feature state for station
    provideState(tourReducer.toursFeatureKey, tourReducer.reducer ), // Feature state for station
    provideState(exhibitionReducer.exhibitionsFeatureKey, exhibitionReducer.reducer ), // Feature state for station
    provideState(contentReducer.contentsFeatureKey, contentReducer.reducer ), // Feature state for station

    provideEffects(StationEffects),
    provideEffects(TourEffects),
    provideEffects(ContentEffects),
    provideEffects(ExhibitionEffects),

    ...(environment.production ? [] : [provideStoreDevtools()]),
    ExhibitionFacade,
    TourFacade,
    StationFacade,
    ContentFacade,
    provideTranslateService({
      loader: provideTranslateHttpLoader({prefix:"./assets/i18n/", suffix:".json"}),
    }),
    provideServiceWorker(
      'ngsw-worker.js',
      { enabled: environment.production, registrationStrategy: 'registerWithDelay:5000' }
    ),
    TranslateService,
    TranslateStore,
    {
      provide: KeycloakConfiguration,
      useFactory: () => new KeycloakConfiguration({basePath: cmsConfig.authUrl})
    },
    {
      provide: RestConfiguration,
      useFactory: () => new RestConfiguration({basePath: cmsConfig.apiUrl})
    },
    {
      provide: SyncConfiguration,
      useFactory: () => new SyncConfiguration({basePath: cmsConfig.sync})
    },
    {
      provide: OrganizationConfiguration,
      useFactory: () => new OrganizationConfiguration({basePath: cmsConfig.organization})
    },

    RestApiModule,
    OrganizationApiModule,
    KeycloakApiModule,
    SyncApiModule
  ]
}).catch(e => console.error(e));
